import React, { Component } from 'react';
import { bool, func, object } from 'prop-types';
import { createRefetchContainer, graphql } from 'react-relay';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { getUserId, cookieJarConstants } from 'dibs-cookie-jar';
import get from 'lodash.get';

import Masthead from '../Masthead/Masthead';
import MessageBanner from '../MessageBanner/MessageBanner';
import TopBar from '../TopBar/TopBar';
import BottomBar from '../BottomBar/BottomBar';
import Drawer from '../Drawer/Drawer';

import { actionCreators } from '../../actions';
import styles from './Header-style.scss';
import * as userTracking from '../../utils/userTracking';
import chat from '../../utils/chat';

const { MASQUERADE_BUYER } = cookieJarConstants;

class Header extends Component {

    constructor(props) {
        super(props);

        this.state = {
            isClient: false,

            fragmentVariables: {
                hasUserId: false,
                showMarketingMessages: this.props.showMarketingMessages,
                userId: "",
                userIds: []
            }
        };
    }

    componentDidMount() {
        this.setState({ isClient: true }); // eslint-disable-line react/no-did-mount-set-state
        this.refetchViewer();
    }

    componentDidUpdate(prevProps) {
        if (this.props.updateUserState && !prevProps.updateUserState) {
            this.refetchViewer();
            this.props.resetUpdateUserState();
        }

        if (get(this.props.viewer, 'user.isVerifiedTrade') && !get(prevProps.viewer, 'user')) {
            this.props.updateUserTypeTrade();
        }
        if (get(this.props.viewer, 'user.isVip') && !get(prevProps.viewer, 'user')) {
            this.props.updateUserTypeVip();
        }

        if (this.state.isClient && !this.props.initialTrackingFired) {
            const { viewer } = this.props;
            const userId = getUserId(document.cookie, MASQUERADE_BUYER);
            const hasUserId = !!userId;
            const hasUser = !!viewer.user;

            if (!hasUserId) {
                userTracking.init({ viewer });

            } else if (hasUser) {
                const { user } = viewer;
                chat.fillUserData(viewer, user);
                userTracking.init({ viewer, user });
            }
        }
    }

    getClassnames() {
        const { disableStickyNav, isMobile } = this.props;
        return {
            wrapperClasses: classnames({
                [styles.wrapper]: true
            }),
            headerClasses: classnames({
                [styles.header]: true,
                [styles.disableSticky]: disableStickyNav,
                ['dbl-resp-width']: !isMobile
            }),
            shadowClasses: classnames({
                [styles.shadow]: true,
                [styles.disableSticky]: disableStickyNav
            }),
            bottomWrapperClasses: classnames({
                [styles.bottomWrapper]: true,
                [styles.disableSticky]: disableStickyNav
            })
        };
    }

    refetchViewer() {
        const userId = getUserId(document.cookie, MASQUERADE_BUYER) || '';
        const hasUserId = !!userId;

        if (hasUserId) {
            this.setState({
                fragmentVariables: {
                    ...this.state.fragmentVariables,
                    isClient: true,
                    hasUserId: !!userId,
                    userId,
                    userIds: [userId]
                }
            }, () => {
                this.props.relay.refetch(this.state.fragmentVariables, null);
            });
        }
    }

    render() {
        const {
            disableStickyNav,
            viewer,
            navigation
        } = this.props;
        const { user = null } = viewer;
        const { isClient, fragmentVariables } = this.state;
        const { hasUserId, showMarketingMessages, userId, userIds } = fragmentVariables;

        const hasUser = !!user;
        const hasRootPendingRefetch = !hasUser && hasUserId;
        const BottomWrapperTag = disableStickyNav ? 'div' : Drawer;
        const { wrapperClasses, headerClasses, shadowClasses, bottomWrapperClasses } = this.getClassnames();

        return (
            <div className={wrapperClasses}>
                <div className={headerClasses}>
                    <Masthead/>
                    <TopBar
                        disableStickyNav={disableStickyNav}
                        hasRootPendingRefetch={hasRootPendingRefetch}
                        hasUser={hasUser}
                        hasUserId={hasUserId}
                        viewer={viewer}
                        isClient={isClient}
                        userId={userId}
                        user={user}
                    />
                    <BottomWrapperTag className={bottomWrapperClasses}>
                        <BottomBar
                            navigation={navigation}
                            viewer={viewer}
                            isClient={isClient}
                            hasUserId={hasUserId}
                            userIds={userIds}
                            hasRootPendingRefetch={hasRootPendingRefetch}
                        />
                    </BottomWrapperTag>
                </div>
                <div className={shadowClasses}></div>
                {showMarketingMessages ?
                    <MessageBanner
                        hasRootPendingRefetch={hasRootPendingRefetch}
                        hasUserId={hasUserId}
                        isClient={isClient}
                        message={null}
                        showMarketingMessages={showMarketingMessages}
                        user={user}
                        userIds={userIds}
                        viewer={viewer}
                    />
                    : null
                }
            </div>
        );
    }
}

Header.propTypes = {
    disableStickyNav: bool,
    initialTrackingFired: bool,
    isClient: bool,
    isMobile: bool,
    navigation: object,
    resetUpdateUserState: func,
    setInitialTrackingFired: func,
    showMarketingMessages: bool,
    relay: object,
    updateUserTypeTrade: func,
    updateUserTypeVip: func,
    updateUserState: bool,
    viewer: object
};

const mapStateToProps = ({ header }) => ({
    isMobile: header.isMobile,
    disableStickyNav: header.disableStickyNav,
    updateUserState: header.updateUserState,
    initialTrackingFired: header.initialTrackingFired
});

const mapDispatchToProps = (dispatch) => ({
    resetUpdateUserState: () => {
        dispatch(actionCreators.setUpdateUserState(false));
    },
    updateUserTypeTrade: () => {
        dispatch(actionCreators.setUserTypeTrade(true));
    },
    updateUserTypeVip: () => {
        dispatch(actionCreators.setUserTypeVip(true));
    },
    setInitialTrackingFired: () => {
        dispatch(actionCreators.setInitialTrackingFired());
    }
});

Header = createRefetchContainer(
    connect(mapStateToProps, mapDispatchToProps)(Header),
    {
        navigation: graphql`
            fragment Header_navigation on Navigation {
                ...BottomBar_navigation
            }
        `,
        viewer: graphql`
            fragment Header_viewer on Viewer {
                ...TopBar_viewer
                ...userTracking_viewer @relay(mask: false)
                ...BottomBar_viewer
                ... on Viewer @include(if: $hasUserId) {
                    ...chat_viewer @relay(mask: false)
                }
                ... on Viewer @include(if: $showMarketingMessages) {
                    ...MessageBanner_viewer
                }
                user(userId: $userId) @include(if: $hasUserId) {
                    ...userTracking_user @relay(mask: false)
                    isVip
                    ...chat_user @relay(mask: false)
                    isVerifiedTrade
                    ... on User @include(if: $showMarketingMessages) {
                        ...MessageBanner_user
                    }
                    ...TopBar_user
                }
            }
        `
    },
        graphql.experimental`
        query HeaderRefetchQuery(
            $userId: String!
            $userIds: [String]!
            $hasUserId: Boolean!
            $showMarketingMessages: Boolean!
            $fetchMarketingMessagesTransactions: Boolean!
            
        ) {
            viewer {
                ...Header_viewer
            }
        }
    `
);

export default Header;



// WEBPACK FOOTER //
// ./src/components/Header/Header.jsx